export const videoPlaybackStartedEvent = "iucore:video-playback-started";

type VideoPlatform = "youtube" | "vimeo";

let embeddedVideoIndex = 0;

interface VideoPlaybackStartedDetail {
    player: Element;
}

/**
 * Announces that a video is about to start playing.
 *
 * @param {Element} player
 * @returns {void}
 */
export function announceVideoPlayback(player: Element): void {
    player.dispatchEvent(
        new CustomEvent<VideoPlaybackStartedDetail>(videoPlaybackStartedEvent, {
            bubbles: true,
            detail: { player },
        }),
    );
}

/**
 * Pauses all native and embedded videos in a container,
 * except for the player that has just started.
 *
 * @param {ParentNode} container
 * @param {Element | null} except
 * @returns {void}
 */
export function pauseVideos(
    container: ParentNode,
    except: Element | null = null,
): void {
    container
        .querySelectorAll<HTMLMediaElement>("video, audio")
        .forEach((media) => {
            if (isExcepted(media, except)) return;
            media.pause();
        });

    container
        .querySelectorAll<HTMLIFrameElement>(embeddedVideoSelector)
        .forEach((iframe) => {
            if (isExcepted(iframe, except)) return;
            pauseEmbeddedVideo(iframe);
        });
}

/**
 * Prepares an embedded player so playback events can be observed.
 *
 * @param {HTMLIFrameElement} iframe
 * @param {VideoPlatform} platform
 * @returns {void}
 */
export function observeEmbeddedVideo(
    iframe: HTMLIFrameElement,
    platform: VideoPlatform,
): void {
    iframe.dataset.videoPlayer = platform;
    iframe.id ||= `iucore-video-player-${++embeddedVideoIndex}`;

    iframe.addEventListener(
        "load",
        () => {
            if (platform === "youtube") {
                iframe.contentWindow?.postMessage(
                    JSON.stringify({
                        event: "listening",
                        id: iframe.id,
                    }),
                    "*",
                );
                return;
            }

            iframe.contentWindow?.postMessage(
                {
                    method: "addEventListener",
                    value: "play",
                },
                "*",
            );
        },
        {
            once: true,
        },
    );
}

/**
 * Resolves whether a message from an embedded player means playback started.
 *
 * @param {unknown} payload
 * @returns {boolean}
 */
export function isVideoPlaybackStartedMessage(payload: unknown): boolean {
    const data = parseMessagePayload(payload);
    if (!data) return false;

    if (data.event === "play") return true;
    if (data.event === "onStateChange" && data.info === 1) return true;

    return (
        data.event === "infoDelivery" &&
        typeof data.info === "object" &&
        data.info !== null &&
        "playerState" in data.info &&
        data.info.playerState === 1
    );
}

/**
 * Finds the embedded player belonging to a postMessage source.
 *
 * @param {ParentNode} container
 * @param {MessageEventSource | null} source
 * @returns {HTMLIFrameElement | null}
 */
export function findEmbeddedVideoBySource(
    container: ParentNode,
    source: MessageEventSource | null,
): HTMLIFrameElement | null {
    if (!source) return null;

    return (
        Array.from(
            container.querySelectorAll<HTMLIFrameElement>(
                embeddedVideoSelector,
            ),
        ).find((iframe) => iframe.contentWindow === source) ?? null
    );
}

const embeddedVideoSelector = [
    "iframe[data-video-player]",
    'iframe[src*="youtube.com"]',
    'iframe[src*="youtube-nocookie.com"]',
    'iframe[src*="vimeo.com"]',
].join(", ");

function isExcepted(player: Element, except: Element | null): boolean {
    if (!except) return false;
    return player === except || except.contains(player);
}

function pauseEmbeddedVideo(iframe: HTMLIFrameElement): void {
    const platform = getVideoPlatform(iframe);
    if (!platform) return;

    if (platform === "youtube") {
        iframe.contentWindow?.postMessage(
            JSON.stringify({
                event: "command",
                func: "pauseVideo",
                args: [],
            }),
            "*",
        );
        return;
    }

    iframe.contentWindow?.postMessage({ method: "pause" }, "*");
}

function getVideoPlatform(iframe: HTMLIFrameElement): VideoPlatform | null {
    if (
        iframe.dataset.videoPlayer === "youtube" ||
        iframe.src.includes("youtube")
    ) {
        return "youtube";
    }
    if (
        iframe.dataset.videoPlayer === "vimeo" ||
        iframe.src.includes("vimeo")
    ) {
        return "vimeo";
    }
    return null;
}

function parseMessagePayload(payload: unknown): Record<string, any> | null {
    if (typeof payload === "object" && payload !== null) {
        return payload as Record<string, any>;
    }
    if (typeof payload !== "string") return null;

    try {
        const parsed = JSON.parse(payload);
        return typeof parsed === "object" && parsed !== null
            ? (parsed as Record<string, any>)
            : null;
    } catch {
        return null;
    }
}
